import { useEffect } from "react";
import { presenceApi } from "@/lib/presenceApi";
import { realtimeClient } from "@/lib/realtimeClient";
import { useAuthStore } from "@/store/authStore";
import { usePresenceStore } from "@/store/presenceStore";
import type { AnchorOnlineStatus, CallStatus } from "@/types";

const HEARTBEAT_INTERVAL_MS = 5000;
const HIDDEN_HEARTBEAT_OFFLINE_DELAY_MS = 30000;

type HeartbeatDocument = Pick<
  Document,
  "visibilityState" | "addEventListener" | "removeEventListener"
>;
type HeartbeatWindow = Pick<
  Window,
  | "setInterval"
  | "clearInterval"
  | "setTimeout"
  | "clearTimeout"
  | "addEventListener"
  | "removeEventListener"
>;

export function shouldSendHTTPHeartbeat(
  isAuthenticated: boolean,
  accessToken?: string | null,
  anchorID?: string | null,
  profileCompleted = false,
) {
  return Boolean(
    isAuthenticated && accessToken && anchorID && profileCompleted,
  );
}

export function httpHeartbeatSessionKey(
  isAuthenticated: boolean,
  hasAccessToken: boolean,
  anchorID?: string | null,
  profileCompleted = false,
) {
  return isAuthenticated && hasAccessToken && anchorID && profileCompleted
    ? anchorID
    : "";
}

export function isHeartbeatPageHidden(
  visibilityState?: DocumentVisibilityState,
) {
  return visibilityState === "hidden";
}

function toAnchorOnlineStatus(callStatus: CallStatus): AnchorOnlineStatus {
  return callStatus === "busy" ? 2 : 1;
}

function sendHTTPHeartbeat(anchorID: string, onlineStatus: AnchorOnlineStatus) {
  return presenceApi.heartbeat({
    anchor_id: anchorID,
    online_status: onlineStatus,
  });
}

export function startHTTPHeartbeatLifecycle({
  anchorID,
  doc,
  win,
  getCallStatus,
  sendHeartbeat,
  hiddenOfflineDelayMs = HIDDEN_HEARTBEAT_OFFLINE_DELAY_MS,
}: {
  anchorID: string;
  doc: HeartbeatDocument;
  win: HeartbeatWindow;
  getCallStatus: () => CallStatus;
  sendHeartbeat: (
    anchorID: string,
    onlineStatus: AnchorOnlineStatus,
  ) => Promise<unknown>;
  hiddenOfflineDelayMs?: number;
}) {
  let heartbeatTimer: number | null = null;
  let hiddenOfflineTimer: number | null = null;
  let hiddenOfflineSent = false;
  let hiddenStartedAt: number | null = null;

  const clearHeartbeatTimer = () => {
    if (heartbeatTimer !== null) {
      win.clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  const clearHiddenOfflineTimer = () => {
    if (hiddenOfflineTimer !== null) {
      win.clearTimeout(hiddenOfflineTimer);
      hiddenOfflineTimer = null;
    }
  };

  const sendCurrentHeartbeat = () => {
    clearHiddenOfflineTimer();
    hiddenOfflineSent = false;
    hiddenStartedAt = null;
    void sendHeartbeat(anchorID, toAnchorOnlineStatus(getCallStatus())).catch(
      () => undefined,
    );
  };

  const sendHiddenOffline = () => {
    if (hiddenOfflineSent) {
      return;
    }
    clearHeartbeatTimer();
    hiddenOfflineSent = true;
    void sendHeartbeat(anchorID, 0).catch(() => undefined);
  };

  const scheduleHiddenOffline = () => {
    if (hiddenStartedAt === null) {
      hiddenStartedAt = Date.now();
    }
    if (hiddenOfflineTimer !== null) {
      return;
    }
    hiddenOfflineTimer = win.setTimeout(() => {
      hiddenOfflineTimer = null;
      if (isHeartbeatPageHidden(doc.visibilityState)) {
        sendHiddenOffline();
      }
    }, hiddenOfflineDelayMs);
  };

  const startHeartbeat = () => {
    clearHeartbeatTimer();
    sendCurrentHeartbeat();
    heartbeatTimer = win.setInterval(() => {
      if (isHeartbeatPageHidden(doc.visibilityState)) {
        if (hiddenOfflineSent) {
          clearHeartbeatTimer();
          return;
        }
        scheduleHiddenOffline();
        if (
          hiddenStartedAt !== null &&
          Date.now() - hiddenStartedAt >= hiddenOfflineDelayMs
        ) {
          sendHiddenOffline();
          return;
        }
        void sendHeartbeat(
          anchorID,
          toAnchorOnlineStatus(getCallStatus()),
        ).catch(() => undefined);
        return;
      }
      sendCurrentHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  };

  const handleVisibilityChange = () => {
    if (isHeartbeatPageHidden(doc.visibilityState)) {
      scheduleHiddenOffline();
      return;
    }
    startHeartbeat();
  };

  const handlePageHide = (event: PageTransitionEvent) => {
    if (event.persisted) {
      clearHiddenOfflineTimer();
      clearHeartbeatTimer();
      return;
    }
    clearHiddenOfflineTimer();
    clearHeartbeatTimer();
    sendHiddenOffline();
  };

  if (isHeartbeatPageHidden(doc.visibilityState)) {
    scheduleHiddenOffline();
    heartbeatTimer = win.setInterval(() => {
      if (isHeartbeatPageHidden(doc.visibilityState)) {
        if (hiddenOfflineSent) {
          clearHeartbeatTimer();
          return;
        }
        scheduleHiddenOffline();
        if (
          hiddenStartedAt !== null &&
          Date.now() - hiddenStartedAt >= hiddenOfflineDelayMs
        ) {
          sendHiddenOffline();
          return;
        }
        void sendHeartbeat(
          anchorID,
          toAnchorOnlineStatus(getCallStatus()),
        ).catch(() => undefined);
        return;
      }
      sendCurrentHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
  } else {
    startHeartbeat();
  }
  doc.addEventListener("visibilitychange", handleVisibilityChange);
  win.addEventListener("pagehide", handlePageHide);

  return () => {
    clearHeartbeatTimer();
    clearHiddenOfflineTimer();
    doc.removeEventListener("visibilitychange", handleVisibilityChange);
    win.removeEventListener("pagehide", handlePageHide);
    void sendHeartbeat(anchorID, 0).catch(() => undefined);
  };
}

export function useRealtimeConnection() {
  const anchor = useAuthStore((state) => state.anchor);
  const anchorID = anchor?.id;
  const accessToken = useAuthStore((state) => state.accessToken);
  const hasAccessToken = Boolean(accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const profileCompleted = anchor?.profile_completed === true;
  const heartbeatSessionKey = httpHeartbeatSessionKey(
    isAuthenticated,
    hasAccessToken,
    anchorID,
    profileCompleted,
  );
  const callStatus = usePresenceStore((state) => state.call_status);
  const connectionStatus = usePresenceStore((state) => state.connection_status);

  useEffect(() => {
    if (!isAuthenticated || !hasAccessToken || !profileCompleted) {
      realtimeClient.disconnect();
    }
  }, [hasAccessToken, isAuthenticated, profileCompleted]);

  useEffect(() => {
    if (!heartbeatSessionKey) {
      return;
    }
    const currentAnchorID = heartbeatSessionKey;

    return startHTTPHeartbeatLifecycle({
      anchorID: currentAnchorID,
      doc: document,
      win: window,
      getCallStatus: () => usePresenceStore.getState().call_status,
      sendHeartbeat: sendHTTPHeartbeat,
    });
  }, [heartbeatSessionKey]);

  useEffect(() => {
    if (!heartbeatSessionKey) {
      return;
    }
    const currentAnchorID = heartbeatSessionKey;

    if (
      typeof document !== "undefined" &&
      isHeartbeatPageHidden(document.visibilityState)
    ) {
      return;
    }
    void sendHTTPHeartbeat(
      currentAnchorID,
      toAnchorOnlineStatus(callStatus),
    ).catch(() => undefined);
  }, [heartbeatSessionKey, callStatus]);

  return {
    connectionStatus,
    connect: realtimeClient.connect.bind(realtimeClient),
    disconnect: realtimeClient.disconnect.bind(realtimeClient),
  };
}
